import { differenceInDays, differenceInMonths, differenceInYears, subDays, subMonths, subYears } from 'date-fns';
import Link from 'next/link';
import React, { ReactNode, useMemo, useState } from 'react';
import {
    Area,
    ComposedChart,
    Legend,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { CartesianViewBox } from 'recharts/types/util/types';

import { Heading } from '@/components/dom/text-elements';
import Button from '@/components/global/button';
import Icon from '@/components/global/icon';
import { LogoWithWordmark } from '@/components/global/logo';
import { ConvertIndexDataToPercent, GetLegendData } from '@/components/graph/helpers';
import { BuildColorMap } from '@/helpers/colors';
import { FormatUtcDate, ParseGqlOptionalUtcDateTime, ParseGqlUtcDate } from '@/helpers/dates';
import { FilterIndexDataOlderThanDate } from '@/helpers/index-data';
import { FormatPercentage } from '@/helpers/numbers';
import { GetIndexSymbolPublicPath } from '@/helpers/paths';
import cn from '@/lib/cn';
import { SITE_BASE_URL } from '@/lib/constants';
import { ProcessedIndexItem } from '@/types/index';

const TIME_RANGE_TO_TEXT = {
    '1m': {
        html: (
            <>
                <div className="font-akqa-sans-serif-bold">&#9679;&nbsp;One</div>
                <div className="font-akqa-serif italic -mt-1">Month</div>
            </>
        ),
        text: 'One Month',
    },
    '1w': {
        html: (
            <>
                <div className="font-akqa-sans-serif-bold">&#9679;&nbsp;One</div>
                <div className="font-akqa-serif italic -mt-1">Week</div>
            </>
        ),
        text: 'One Week',
    },
    '1y': {
        html: (
            <>
                <div className="font-akqa-sans-serif-bold">&#9679;&nbsp;One</div>
                <div className="font-akqa-serif italic -mt-1">Year</div>
            </>
        ),
        text: 'One Year',
    },
    '3m': {
        html: (
            <>
                <div className="font-akqa-sans-serif-bold">&#9679;&nbsp;Three</div>
                <div className="font-akqa-serif italic -mt-1">Months</div>
            </>
        ),
        text: 'Three Months',
    },
    '3y': {
        html: (
            <>
                <div className="font-akqa-sans-serif-bold">&#9679;&nbsp;Three</div>
                <div className="font-akqa-serif italic -mt-1">Years</div>
            </>
        ),
        text: 'Three Years',
    },
    '6m': {
        html: (
            <>
                <div className="font-akqa-sans-serif-bold">&#9679;&nbsp;Six</div>
                <div className="font-akqa-serif italic -mt-1">Months</div>
            </>
        ),
        text: 'Six Months',
    },
    all: {
        html: (
            <>
                <div className="font-akqa-sans-serif-bold">&#9679;&nbsp;All</div>
            </>
        ),
        text: 'All',
    },
};

type ChartProps = {
    color?: 'white' | 'black' | 'transparent';
    cursor?: string;
    defaultIndexName?: string;
    height?: number;
    indexData: Array<ProcessedIndexItem>;
    symbolList: Array<string>;
    showLogo?: boolean;
    showTimeSelectors?: boolean;
    defaultTimeSelector?: keyof typeof TIME_RANGE_TO_TEXT;
    indexSymbol: string;
    firstEffectiveAt: string;
    holdingsCount: number;
};

interface TooltipPayload extends Payload<number | Array<number>, string> {
    name?: string | undefined;
    stroke?: string | undefined;
    value?: number | Array<number> | undefined;
}

const FormatDate = (tickItem: string, formatString = 'dd:MMM:yy') => {
    const date = ParseGqlUtcDate(tickItem);
    const formatedDate = FormatUtcDate(date, formatString) as string;
    return formatedDate.toUpperCase();
};

const generateYTicks = (min: number, max: number): Array<number> => {
    const ticks = [];
    const roundedMin = Math.ceil(min / 10) * 10;
    const roundedMax = Math.ceil(max / 10) * 10;
    let interval = 10;

    if (Math.abs(roundedMin + -roundedMax) / interval > 14) {
        interval = 20;
    }

    for (let i = roundedMin; i < roundedMax + interval; i += interval) {
        ticks.push(i);
    }

    return ticks;
};

const generateXTicks = (indexData: Array<ProcessedIndexItem>, tickCount = 5) => {
    const velocity = Math.round(indexData.length / tickCount);
    const ticks = [indexData[0].asOfDate];

    for (let i = 1; i < tickCount; i++) {
        if (i * velocity < indexData.length) {
            ticks.push(indexData[i * velocity].asOfDate);
        }
    }

    return ticks;
};

const AkqaGraph = ({
    cursor,
    defaultIndexName,
    height = 200,
    indexData,
    symbolList,
    defaultTimeSelector = '3y',
    firstEffectiveAt,
    holdingsCount,
}: ChartProps) => {
    const indexSymbol = symbolList[0] || (defaultIndexName ?? '');
    const sortedData: Array<ProcessedIndexItem> = useMemo(
        () => indexData.slice().sort((p1, p2) => (p1.asOfDate > p2.asOfDate ? 1 : -1)),
        [indexData]
    );
    const firstEffectiveAtDate = ParseGqlOptionalUtcDateTime(firstEffectiveAt);

    const lastDateOfData = ParseGqlUtcDate(sortedData[sortedData.length - 1].asOfDate);
    const TIME_RANGES = useMemo(() => {
        return {
            '1m': subMonths(lastDateOfData, 1),
            '1w': subDays(lastDateOfData, 7),
            '1y': subYears(lastDateOfData, 1),
            '3m': subMonths(lastDateOfData, 3),
            '3y': subYears(lastDateOfData, 3),
            '6m': subMonths(lastDateOfData, 6),
            all: null,
        };
    }, [lastDateOfData]);

    const [selectedTimeRange, setSelectedTimeRange] = useState<keyof typeof TIME_RANGES>(defaultTimeSelector);
    const filteredData = FilterIndexDataOlderThanDate(sortedData, TIME_RANGES[selectedTimeRange]);
    const percentData = ConvertIndexDataToPercent(filteredData);

    const colorMap = BuildColorMap('#E59FCF', symbolList, indexSymbol, ['#3B7BED', '#FFCC9A', '#1F51FF']);

    // store some dates we will use throughout the graph
    const firstDateOfData = ParseGqlUtcDate(sortedData[0].asOfDate);

    const daysInData = differenceInDays(lastDateOfData, firstDateOfData);
    const monthsInData = differenceInMonths(lastDateOfData, firstDateOfData);
    const yearsInData = useMemo(
        () => differenceInYears(lastDateOfData, firstDateOfData),
        [firstDateOfData, lastDateOfData]
    );

    const minValue = Math.min(
        ...percentData.map((i) => {
            const values = Object.values(i.symbolValues);
            return Math.min(...values);
        })
    );

    const maxValue = Math.max(
        ...percentData.map((i) => {
            const values = Object.values(i.symbolValues);
            return Math.max(...values);
        })
    );
    const MIN_EXTRA = 10;

    const getArea = (symbol: string, min: number) => {
        const dataKey = (item: ProcessedIndexItem) => {
            return [min - MIN_EXTRA, item.symbolValues[symbol]];
        };

        return (
            <>
                <defs>
                    <linearGradient
                        id="akqaGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                    >
                        <stop
                            offset="45%"
                            stopColor="#E59FCF"
                            stopOpacity={0.9}
                        />
                        <stop
                            offset="100%"
                            stopColor="#E59FCF"
                            stopOpacity={0.1}
                        />
                    </linearGradient>
                </defs>
                <Area
                    fill="url(#akqaGradient)"
                    stroke="#E59FCF"
                    strokeWidth={1}
                    fillOpacity={1}
                    dataKey={dataKey}
                    name={symbol}
                    key={symbol}
                    legendType="circle"
                    isAnimationActive={false}
                    activeDot={{ r: 5, stroke: '#d2fa51' }}
                />
            </>
        );
    };

    const getLines = ([...symbols]: Array<string>) => {
        const indexSymbolPosition = symbols.indexOf(indexSymbol);
        if (indexSymbolPosition !== -1) {
            symbols.splice(indexSymbolPosition, 1);
        }
        const lines = symbols.map((symbol) => {
            const dataKey = (item: ProcessedIndexItem) => {
                if (symbol === indexSymbol) {
                    return null;
                }
                return item.symbolValues[symbol];
            };

            return (
                <Area
                    type="monotone"
                    opacity={1}
                    fill="none"
                    dot={false}
                    dataKey={dataKey}
                    name={symbol}
                    key={symbol}
                    stroke={colorMap.get(symbol)}
                    legendType="circle"
                    isAnimationActive={false}
                    strokeWidth={1}
                    connectNulls={true}
                    activeDot={{ stroke: '#d2fa51' }}
                />
            );
        });
        return lines;
    };

    const timeRangeFirstLiveDate = percentData.find(({ isBackcalculated }) => !isBackcalculated)?.asOfDate;
    const hasBackcalculatedData = !timeRangeFirstLiveDate || timeRangeFirstLiveDate > percentData[0].asOfDate;

    const customToolTip = ({ active, payload, label }: TooltipProps<number | Array<number>, string>): JSX.Element => {
        if (active && payload && payload.length) {
            // use a map to handle duplicates
            const linesMap: { [key: string]: ReactNode } = {};
            payload.slice(0).forEach((element: TooltipPayload, index) => {
                if (element.value === undefined || element.value === null) return;
                const elementName = element.name as string;
                if (elementName in linesMap) return;
                const tooltipText = elementName || indexSymbol;

                let value = element.value;
                if (Array.isArray(value)) {
                    // if it's an array we need to set the value to the last time
                    value = value[value.length - 1];
                }

                linesMap[elementName] = (
                    <li key={`tool-tip-time--${elementName}`}>
                        <span
                            className="flex flex-column justify-between items-end font-akqa-sans-serif"
                            style={{
                                color: colorMap.get(elementName as string),
                            }}
                        >
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center justify-start">
                                    <span className="mr-1 text-2xl">&#9675;</span>
                                    <span className={cn({ 'font-akqa-sans-serif-bold': index === 0 })}>
                                        {tooltipText}
                                    </span>
                                </div>
                                <p className="ml-4 font-akqa-mono text-right flex-grow">
                                    {value > 0 ? '+' : ''}
                                    {FormatPercentage(value)}
                                </p>
                            </div>
                        </span>
                    </li>
                );
            });

            const lines = Object.values(linesMap);

            return (
                <div className="bg-akqa-green-dark text-white p-4 rounded-xl outline-none ">
                    <div className="flex flex-column">
                        <h3 className="mr-4 text-akqa-acid">
                            <span className="font-akqa-sans-serif-bold">{FormatDate(label, 'MMM dd')} </span>
                            <span className="font-akqa-serif text-base">{FormatDate(label, ', yyyy')}</span>
                        </h3>
                    </div>
                    <ul>{lines}</ul>
                </div>
            );
        }

        return <></>;
    };

    type TimeLabelProps = {
        timeLabel: keyof typeof TIME_RANGE_TO_TEXT;
    };
    const TimeSelectorButton = ({ timeLabel }: TimeLabelProps) => {
        const rangeLabel = TIME_RANGE_TO_TEXT[timeLabel];

        return (
            <>
                <Button
                    // TODO: TRACKING
                    type="action"
                    color="transparent"
                    onClick={() => setSelectedTimeRange(timeLabel)}
                    isDisabled={daysInData < 7}
                    className={cn(
                        'text-akqa-acid active:text-akqa-acid hover:bg-brand-black-off hover:!text-akqa-acid rounded-xl uppercase !px-3 !py-1.5 !text-xs md:text-sm',
                        {
                            'bg-akqa-acid !text-brand-black': selectedTimeRange === timeLabel,
                        }
                    )}
                >
                    <div className="text-right">{rangeLabel.html}</div>
                </Button>
            </>
        );
    };
    const TimeSelectors = (
        <div className="flex md:justify-end text-xs mb-6">
            <div className="flex justify-evenly xs:justify-between bg-akqa-green-dark rounded-xl p-1 md:p-2 w-full md:w-auto gap-2 flex-wrap">
                {daysInData >= 7 && (
                    <span className="hidden sm:block">
                        <TimeSelectorButton timeLabel="1w" />
                    </span>
                )}
                {monthsInData >= 1 && <TimeSelectorButton timeLabel="1m" />}
                {monthsInData >= 3 && (
                    <span className="hidden sm:block">
                        <TimeSelectorButton timeLabel="3m" />
                    </span>
                )}
                {monthsInData >= 6 && <TimeSelectorButton timeLabel="6m" />}
                {yearsInData >= 1 && <TimeSelectorButton timeLabel="1y" />}
                {yearsInData >= 3 && <TimeSelectorButton timeLabel="3y" />}
                <TimeSelectorButton timeLabel="all" />
            </div>
        </div>
    );

    type PercentChangeProps = {
        symbolList: Array<string>;
        indexSymbol: string;
        selectedTimeRange: keyof typeof TIME_RANGES;
        data: Array<ProcessedIndexItem>;
    };

    const PercentChange = ({ indexSymbol, selectedTimeRange, data }: PercentChangeProps) => {
        if (data.length < 2) {
            return <></>;
        }

        const firstItem = data[0];
        const lastItem = data[data.length - 1];

        const indexChange = lastItem.symbolValues[indexSymbol] - firstItem.symbolValues[indexSymbol];

        return (
            <div className="flex-shrink-0 text-left md:py-6 flex md:flex-col items-end md:items-start">
                <Heading
                    importance={3}
                    className={cn(
                        'whitespace-nowrap mb-1 font-akqa-sans-serif-bold flex justify-start items-center text-4xl',
                        {
                            'text-akqa-acid': indexChange > 0,
                            'text-akqa-pink': indexChange < 0,
                        }
                    )}
                >
                    <Icon
                        type="dropDownArrow"
                        rotate={indexChange > 0 ? 180 : 0}
                        size="lg"
                        className={cn('mr-3 pt-[2px]', {
                            'text-akqa-acid': indexChange > 0,
                            'text-akqa-pink': indexChange < 0,
                        })}
                        color="inherit"
                    />
                    <span>{FormatPercentage(indexChange, { suffix: '' })}</span>
                    <span className="text-3xl">%</span>
                </Heading>
                <div
                    className={cn('text-sm font-akqa-serif text-left ml-2 mb-2', {
                        'text-akqa-acid': indexChange > 0,
                        'text-akqa-pink': indexChange < 0,
                    })}
                >
                    {TIME_RANGE_TO_TEXT[selectedTimeRange].text}
                </div>
            </div>
        );
    };

    type customLabelProps = {
        viewBox: CartesianViewBox;
    };

    const renderCustomLabel = ({ viewBox }: customLabelProps) => {
        return (
            <g>
                <foreignObject
                    y={25}
                    x={60}
                    width={viewBox.width}
                    height={40}
                >
                    <p className="text-white font-akqa-mono text-[10px] text-center px-4">
                        Index <span className="text-akqa-acid">before inception</span> date
                    </p>
                </foreignObject>
            </g>
        );
    };

    return (
        <div className="relative">
            <div className="flex flex-col md:flex-row">
                <div className="flex flex-col md:w-80 mb-2 md:mt-20 justify-between px-8">
                    <div>
                        <div className="flex justify-between">
                            <img
                                src="/assets/akqa/logo.png"
                                alt="PinkChip US"
                                className="mb-4 w-[245px]"
                            />
                        </div>
                        <ul className=" text-akqa-acid text-sm uppercase flex flex-row md:flex-col mb-4 justify-between gap-2">
                            <li>
                                <span className="font-akqa-serif text-[13px]">Ticker:</span>
                                <br />
                                <span className="font-akqa-sans-serif-bold">{indexSymbol}</span>
                            </li>
                            <li>
                                <span className="font-akqa-serif text-[13px]">Inception&nbsp;Date:</span>
                                <br />
                                <span className="font-akqa-sans-serif-bold">
                                    {firstEffectiveAtDate && FormatUtcDate(firstEffectiveAtDate, 'dd:MM:yyyy')}
                                </span>
                            </li>
                            <li>
                                <span className="font-akqa-serif text-[13px]">Weighting:</span>
                                <br />
                                <span className="font-akqa-sans-serif-bold text-xs">Custom</span>
                            </li>
                            <li>
                                <span className="font-akqa-serif text-[13px]">No.&nbsp;of&nbsp;holdings:</span>
                                <br />
                                <span className="font-akqa-sans-serif-bold">{holdingsCount}</span>
                            </li>
                        </ul>
                    </div>
                    <div className="md:pb-14">
                        <PercentChange
                            symbolList={symbolList}
                            indexSymbol={indexSymbol}
                            selectedTimeRange={selectedTimeRange}
                            data={percentData}
                        />
                    </div>
                </div>
                <div className="relative flex-grow flex flex-col">
                    <div className="px-6 md:px-4">{TimeSelectors}</div>
                    <div className="overflow-x-scroll scrollbar-thin scrollbar-thumb-akqa-acid mb-2 pb-2 md:pb-0 mx-0 px-6">
                        <div className="w-[700px] md:w-full overflow-hidden relative">
                            <div
                                className={`absolute w-full left-[60px] top-0 bg-akqa-graph bg-cover`}
                                style={{ height: `${height - 83}px` }}
                            ></div>
                            <ResponsiveContainer
                                width="100%"
                                height={height}
                            >
                                <ComposedChart
                                    data={percentData}
                                    style={{
                                        cursor: cursor && cursor,
                                        fontFamily: 'Satoshi-Regular',
                                        fontSize: '14px',
                                        overflow: 'visible',
                                    }}
                                    margin={{ bottom: 0, left: 0, right: 0, top: 0 }}
                                >
                                    {hasBackcalculatedData && (
                                        <ReferenceArea
                                            x1={percentData[0]?.asOfDate}
                                            x2={timeRangeFirstLiveDate}
                                            y1={minValue - MIN_EXTRA}
                                            label={renderCustomLabel}
                                            fill="#E9E0CF"
                                            fillOpacity={0.1}
                                        />
                                    )}
                                    <XAxis
                                        id="dateAxis"
                                        name="Date"
                                        type="category"
                                        dataKey="asOfDate"
                                        ticks={generateXTicks(percentData, 6)}
                                        interval="preserveStartEnd"
                                        tickFormatter={(value) => FormatDate(value)}
                                        allowDuplicatedCategory={false}
                                        axisLine={false}
                                        tickSize={0}
                                        tick={{ fill: '#ADFF00', fontFamily: 'SpaceMono-Regular', fontSize: 11 }}
                                        tickMargin={20}
                                        className="font-akqa-mono"
                                        // padding={{ left: 20, right: 20 }}
                                    />
                                    <YAxis
                                        id="percentAxis"
                                        domain={['dataMin', 'dataMax + 10']}
                                        type="number"
                                        tickFormatter={(value) => FormatPercentage(value, { places: 0 })}
                                        axisLine={false}
                                        tickMargin={30}
                                        interval="preserveStart"
                                        ticks={generateYTicks(minValue - MIN_EXTRA, maxValue)}
                                        tickCount={6}
                                        tick={{ fill: '#ADFF00', fontFamily: 'SpaceMono-Regular', fontSize: 11 }}
                                        tickSize={0}
                                        className="font-akqa-mono"
                                    />

                                    <ReferenceLine
                                        segment={[
                                            {
                                                x: percentData[0]?.asOfDate,
                                                y: minValue - MIN_EXTRA,
                                            },
                                            {
                                                x: percentData[0]?.asOfDate,
                                                y: percentData[0]?.symbolValues[indexSymbol],
                                            },
                                        ]}
                                        stroke="#E59FCF"
                                        strokeWidth={1}
                                    />
                                    <ReferenceLine
                                        segment={[
                                            {
                                                x: percentData[percentData.length - 1]?.asOfDate,
                                                y: minValue - MIN_EXTRA,
                                            },
                                            {
                                                x: percentData[percentData.length - 1]?.asOfDate,
                                                y: percentData[percentData.length - 1]?.symbolValues[indexSymbol],
                                            },
                                        ]}
                                        stroke="#E59FCF"
                                    />

                                    <Legend
                                        verticalAlign="bottom"
                                        iconSize={6}
                                        align="left"
                                        wrapperStyle={{
                                            fontFamily: 'ClashGrotesk-Regular',
                                            paddingLeft: '30px',
                                            paddingTop: '30px',
                                        }}
                                        payload={GetLegendData(symbolList, indexSymbol, colorMap)}
                                        className="font-akqa-sans-serif"
                                        formatter={(value, _entry, index) => {
                                            if (index === 0) {
                                                return <span className="font-akqa-sans-serif-bold">{value}</span>;
                                            }
                                            return <span>{value}</span>;
                                        }}
                                    />
                                    <Tooltip
                                        content={customToolTip}
                                        wrapperStyle={{ outline: 'none' }}
                                        cursor={{ stroke: '#d2fa51', strokeWidth: 1 }}
                                        itemStyle={{ color: '#d2fa51' }}
                                    />
                                    {getArea(indexSymbol, minValue)}
                                    {getLines(symbolList)}
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            <Link
                href={`${SITE_BASE_URL}${GetIndexSymbolPublicPath(indexSymbol)}`}
                target="_blank"
                className="flex no-underline text-xs ml-4"
            >
                <span className="text-akqa-pink no-underline mr-2"> Powered by</span>
                <div className="flex flex-grow">
                    <LogoWithWordmark
                        size="xs"
                        className="text-akqa-pink"
                    />
                </div>
            </Link>
        </div>
    );
};

export default AkqaGraph;
